import { useNotification } from 'admin';
import {
  type ErrorType,
  getApiInspectionsV1CompaniesListQueryKey,
  type InspectionCompany,
  isApiFormError,
  isFieldErrorResponse,
  useApiInspectionsV1CompaniesCreate,
  useApiInspectionsV1CompaniesUpdate,
  useQueryClient,
} from 'api';
import { CheckboxField, Form, TextField, useForm } from 'form';
import { ActionRow, Button, Typography } from 'ui';

type FormValues = {
  name: string;
  is_active: boolean;
};

type BaseProps = {
  onClose: () => void;
  onSave: (data: InspectionCompany) => void;
};

type CreateProps = BaseProps & {
  action: 'create';
  company?: never;
};

type EditProps = BaseProps & {
  action: 'edit';
  company: InspectionCompany;
};

const isEditMode = (props: CreateProps | EditProps): props is EditProps => {
  return props.action === 'edit';
};
export const InspectionCompanyMutate = (props: CreateProps | EditProps) => {
  const { open } = useNotification();
  const { onClose, onSave, action = 'create', company } = props;
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    defaultValues: {
      is_active: company?.is_active ?? true,
      name: company?.name ?? '',
    },
  });

  const handleError = (error: ErrorType<unknown>) => {
    if (isApiFormError(error)) {
      open?.({
        description: error?.response?.data.detail,
        message: 'Error signing in',
        type: 'error',
      });
      return;
    }
    if (isFieldErrorResponse(error)) {
      Object.keys(error.response?.data).forEach((field) => {
        form.setError(field as keyof FormValues, { message: error.response.data[field] });
      });
      if (error.response?.data.non_field_errors) {
        open?.({
          description: error?.response?.data.non_field_errors[0],
          message: 'Error signing in',
          type: 'error',
        });
      }
      return;
    }

    open?.({
      message: 'Error signing in',
      type: 'error',
    });
  };

  const handleSuccess = (data: InspectionCompany) => {
    onSave?.(data);
    queryClient.invalidateQueries({
      queryKey: getApiInspectionsV1CompaniesListQueryKey(),
    });
  };

  const { mutate: create, isPending: isCreatePending } = useApiInspectionsV1CompaniesCreate({
    mutation: {
      onError: handleError,
      onSuccess: handleSuccess,
    },
  });

  const { mutate: edit, isPending: isEditPending } = useApiInspectionsV1CompaniesUpdate({
    mutation: {
      onError: handleError,
      onSuccess: handleSuccess,
    },
  });

  const handleSubmit = (values: FormValues) => {
    if (isEditMode(props)) {
      edit({
        data: values,
        id: props.company.id,
      });
      return;
    }
    create({
      data: values,
    });
  };

  const handleCancel = () => {
    onClose?.();
  };

  return (
    <Form {...form} onSubmit={handleSubmit}>
      <Typography variant="h6">{action === 'create' ? 'Add Company' : 'Edit Company'}</Typography>
      <TextField
        name="name"
        required
        margin="normal"
        type="text"
        label="Name"
        disabled={!!company}
        InputLabelProps={{ shrink: true }}
        inputProps={{ 'data-testid': 'company-name' }}
      />

      <CheckboxField name="is_active" label="Active" />

      <ActionRow>
        <Button fullWidth variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button fullWidth type="submit" variant="primary" disabled={isCreatePending || isEditPending}>
          Save
        </Button>
      </ActionRow>
    </Form>
  );
};
